import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";

/**
 * EditWorkEntryDialog — admin-only edition of an existing work entry.
 */
const EditWorkEntryDialog = ({
  entry,
  onClose,
  onSaved,
  employees,
  orders,
  savs,
  suppliers,
}) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry) { setDraft(null); return; }
    setDraft({
      start_time: entry.start_time || "",
      end_time: entry.end_time || "",
      entry_type: entry.entry_type || "custom",
      reference_id: entry.reference_id || "",
      custom_label: entry.custom_label || "",
      comment: entry.comment || "",
      _order_search: "",
    });
  }, [entry]);

  if (!entry || !draft) {
    return (
      <Dialog open={false} onOpenChange={() => onClose?.()}>
        <DialogContent />
      </Dialog>
    );
  }

  const handleSave = async () => {
    const t = draft.entry_type;
    if (["commande", "sav", "fournisseur"].includes(t) && !draft.reference_id) {
      toast.error(`Sélectionnez ${t === "fournisseur" ? "un fournisseur" : `une ${t === "sav" ? "intervention SAV" : "commande"}`}`);
      return;
    }
    const patch = {
      start_time: draft.start_time,
      end_time: draft.end_time,
      entry_type: t,
      reference_id: ["commande", "sav", "fournisseur"].includes(t) ? draft.reference_id : null,
      custom_label: t === "custom" ? (draft.custom_label || null) : null,
      comment: (draft.comment || "").trim() || null,
    };
    try {
      setSaving(true);
      await api.updateWorkEntry(entry.id, patch);
      toast.success("Entrée modifiée");
      onClose?.();
      onSaved?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Échec"));
    } finally {
      setSaving(false);
    }
  };

  const employeeName = (() => {
    const e = employees.find(emp => emp.id === entry.employee_id);
    return e ? `${e.first_name || ""} ${e.last_name || ""}`.trim() : "";
  })();

  return (
    <Dialog open={!!entry} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-[560px]" data-testid="edit-entry-dialog">
        <DialogHeader>
          <DialogTitle>Modifier l'entrée</DialogTitle>
          <DialogDescription>
            {entry.date} · salarié {employeeName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Début</Label>
              <Input type="time" value={draft.start_time}
                onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
                data-testid="edit-start-time" />
            </div>
            <div>
              <Label className="text-xs">Fin</Label>
              <Input type="time" value={draft.end_time}
                onChange={(e) => setDraft({ ...draft, end_time: e.target.value })}
                data-testid="edit-end-time" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Type d'intervention</Label>
            <Select
              value={draft.entry_type}
              onValueChange={(v) => setDraft({ ...draft, entry_type: v, reference_id: "", custom_label: "", _order_search: "" })}
            >
              <SelectTrigger data-testid="edit-entry-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="commande">Commande</SelectItem>
                <SelectItem value="sav">SAV</SelectItem>
                <SelectItem value="fournisseur">Fournisseur</SelectItem>
                <SelectItem value="atelier">Atelier</SelectItem>
                <SelectItem value="custom">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {draft.entry_type === "commande" && (() => {
            const q = (draft._order_search || "").trim().toLowerCase();
            const selected = orders.find((o) => o.id === draft.reference_id);
            if (selected && !q) {
              return (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-800 truncate max-w-[400px]">
                    {selected.client_name}
                    {selected.city ? ` — ${selected.city}` : ""}
                    {selected.order_type_name ? ` — ${selected.order_type_name}` : ""}
                  </Badge>
                  <Button size="sm" variant="ghost" onClick={() => setDraft({ ...draft, reference_id: "", _order_search: "" })}>Changer</Button>
                </div>
              );
            }
            const filtered = q
              ? orders.filter((o) => {
                  const r = (o.reference || "").toLowerCase();
                  const c = (o.client_name || "").toLowerCase();
                  const city = (o.city || "").toLowerCase();
                  const tn = (o.order_type_name || "").toLowerCase();
                  return r.includes(q) || c.includes(q) || city.includes(q) || tn.includes(q);
                }).slice(0, 20)
              : [];
            return (
              <div>
                <Label className="text-xs">Commande (tapez nom client, ville ou type)</Label>
                <div className="relative">
                  <Input
                    placeholder="Tapez pour rechercher…"
                    value={draft._order_search}
                    onChange={(e) => setDraft({ ...draft, _order_search: e.target.value, reference_id: "" })}
                    autoComplete="off"
                    data-testid="edit-order-search"
                  />
                  {q && filtered.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg">
                      {filtered.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 border-b last:border-b-0 transition-colors"
                          onClick={() => setDraft({ ...draft, reference_id: o.id, _order_search: "" })}
                        >
                          <span className="font-medium">{o.client_name}</span>
                          {o.city && <span className="text-muted-foreground"> — {o.city}</span>}
                          {o.order_type_name && <span className="text-indigo-700"> — {o.order_type_name}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {draft.entry_type === "sav" && (
            <div>
              <Label className="text-xs">SAV (en cours)</Label>
              <Select value={draft.reference_id} onValueChange={(v) => setDraft({ ...draft, reference_id: v })}>
                <SelectTrigger data-testid="edit-sav-select"><SelectValue placeholder="— Choisir un SAV —" /></SelectTrigger>
                <SelectContent>
                  {(savs || []).filter((s) => !["termine", "annule"].includes(s.status)).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      SAV {s.client_name || s.order_reference || s.id.slice(0, 8)}
                      {s.intervention_date ? ` — ${s.intervention_date}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {draft.entry_type === "fournisseur" && (
            <div>
              <Label className="text-xs">Fournisseur</Label>
              <Select value={draft.reference_id} onValueChange={(v) => setDraft({ ...draft, reference_id: v })}>
                <SelectTrigger data-testid="edit-supplier-select"><SelectValue placeholder="— Choisir un fournisseur —" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name || s.company_name || s.id.slice(0, 8)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {draft.entry_type === "custom" && (
            <div>
              <Label className="text-xs">Description</Label>
              <Input
                value={draft.custom_label}
                onChange={(e) => setDraft({ ...draft, custom_label: e.target.value })}
                placeholder="Description libre"
              />
            </div>
          )}

          <div>
            <Label className="text-xs">Commentaire</Label>
            <Input
              value={draft.comment}
              onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
              placeholder="Note interne (visible par tous)"
              maxLength={500}
              data-testid="edit-comment"
            />
          </div>

          {Array.isArray(entry.history) && entry.history.length > 0 && (
            <div className="border-t pt-3">
              <Label className="text-xs flex items-center gap-1.5 mb-2">
                <span className="text-indigo-700">Historique</span>
                <Badge variant="outline" className="text-[10px]">{entry.history.length}</Badge>
              </Label>
              <div className="max-h-40 overflow-y-auto text-xs space-y-1.5 bg-zinc-50 rounded-md p-2 border">
                {entry.history.slice().reverse().map((h, idx) => (
                  <div key={idx} className="border-b border-zinc-200 pb-1 last:border-0">
                    <div className="text-muted-foreground text-[11px]">
                      {h.at ? new Date(h.at).toLocaleString("fr-FR") : ""} · <span className="font-medium">{h.user_name || h.user_email}</span>
                    </div>
                    <ul className="text-[11px] pl-2 mt-0.5">
                      {(h.changes || []).map((c, j) => (
                        <li key={j}>
                          <span className="text-indigo-700">{c.field}</span> : <span className="line-through opacity-70">{c.old ?? "∅"}</span> → <span className="font-medium">{c.new ?? "∅"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="edit-entry-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditWorkEntryDialog;
